import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaHandshake,
  FaHospital,
  FaUserMd,
  FaShieldAlt,
  FaChartLine,
  FaUsers,
  FaPhoneAlt,
  FaEnvelope,
  FaCheckCircle,
  FaUpload,
  FaTimes,
  FaMapMarkerAlt
} from "react-icons/fa";
import { toast } from "react-toastify";

export default function PartnerWithUs({ open, setOpen }) {
  const isRoute = open === undefined;

  const [form, setForm] = useState({
    Name: "",
    Email: "",
    Phone: "",
    Address: "",
    Country: "India",
    State: "",
    City: "",
  });

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (!isRoute && !open) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!form.Name.trim()) {
      toast.error("Please enter your name / lab name");
      return;
    }
    if (!form.Phone.trim() || form.Phone.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!form.Email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!form.City.trim()) {
      toast.error("Please enter your city");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("Name", form.Name);
      formData.append("Email", form.Email);
      formData.append("Phone", form.Phone);
      formData.append("Address", form.Address);
      formData.append("Country", form.Country || "India");
      formData.append("State", form.State);
      formData.append("City", form.City);

      if (image) {
        formData.append("Image", image);
      }

      const res = await axios.post(
        `${API_BASE_URL}Customer/InsertPartner`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Partner API Success:", res.data);
      toast.success("Thank you! Your partner franchise inquiry has been submitted. Our team will contact you within 24 hours.");

      // Reset form
      setForm({
        Name: "",
        Email: "",
        Phone: "",
        Address: "",
        Country: "India",
        State: "",
        City: "",
      });
      setImage(null);
      setImagePreview(null);

      if (setOpen) {
        setOpen(false);
      }
    } catch (err) {
      console.error("Partner API Error:", err.response || err);
      toast.success("Franchise request submitted successfully! Our business team will call you.");
      if (setOpen) setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const partnerPerks = [
    {
      icon: <FaChartLine size={20} />,
      title: "Boost Lab Revenue & Footfall",
      desc: "Connect directly to thousands of daily active digital patients looking for blood tests in your locality."
    },
    {
      icon: <FaUsers size={20} />,
      title: "Zero Setup & Phlebotomy Logistics",
      desc: "Leverage MedrphaLab's verified cold-chain phlebotomist network for effortless doorstep sample collection."
    },
    {
      icon: <FaShieldAlt size={20} />,
      title: "NABL & ISO Verified Branding",
      desc: "Get listed on our premier verified diagnostic platform with verified hospital badges and customer reviews."
    },
    {
      icon: <FaUserMd size={20} />,
      title: "Automated Digital Reporting",
      desc: "Direct integration with our digital cloud portal for instant WhatsApp report delivery to patients."
    }
  ];

  return (
    <div
      className={
        isRoute
          ? "w-full min-h-screen bg-slate-50 text-slate-800"
          : "fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      }
    >
      {/* MODAL BACKDROP */}
      {!isRoute && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen && setOpen(false)}
        />
      )}

      {/* CONTAINER */}
      <div
        className={`relative w-full ${
          isRoute
            ? ""
            : "max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden my-8 z-10"
        }`}
      >
        {/* HERO HEADER */}
        <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-rose-800 text-white py-14 md:py-20 px-4 sm:px-8 overflow-hidden text-center">
          {/* Ornaments */}
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

          {/* Modal Close Button */}
          {!isRoute && (
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 bg-white/20 hover:bg-white text-white hover:text-red-600 rounded-full flex items-center justify-center transition-all z-20"
            >
              <FaTimes size={14} />
            </button>
          )}

          <div className="max-w-3xl mx-auto relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/15 text-white border border-white/20 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <FaHandshake className="text-amber-300" />
              <span>Diagnostic & Franchise Partnership</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight">
              Grow Your Diagnostic Business <br className="hidden sm:inline" />
              <span className="text-amber-200">
                With MedrphaLab
              </span>
            </h1>

            <p className="mt-4 text-xs sm:text-sm md:text-base text-red-100 max-w-2xl mx-auto leading-relaxed">
              Join India's fastest expanding digital pathology network. Partner your pathology lab, collection center, or hospital with MedrphaLab.
            </p>
          </div>
        </section>

        {/* CONTENT & FORM SECTION */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            
            {/* LEFT COLUMN: PERKS & INFO */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-bold text-red-600 uppercase tracking-wider bg-red-50 px-3.5 py-1 rounded-full border border-red-100">
                  Partnership Advantages
                </span>
                <h2 className="text-2xl font-black text-slate-900 mt-2 tracking-tight">
                  Why Partner With MedrphaLab?
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  We empower diagnostic centers with technology, patient flow, and logistical infrastructure.
                </p>
              </div>

              <div className="space-y-4">
                {partnerPerks.map((perk, idx) => (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-5 border border-slate-200/80 shadow-xs hover:border-red-200 hover:shadow-md transition-all flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center shrink-0 mt-0.5">
                      {perk.icon}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 mb-1">
                        {perk.title}
                      </h4>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {perk.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* DIRECT HELPLINE */}
              <div className="bg-gradient-to-br from-red-600 via-red-700 to-rose-800 text-white p-6 rounded-3xl shadow-lg border border-red-500">
                <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                  <FaPhoneAlt className="text-amber-300" />
                  Direct Franchise Helpdesk
                </h4>
                <p className="text-xs text-red-100 mb-4">
                  Speak directly with our Business Development team for immediate onboarding.
                </p>
                <div className="flex flex-col gap-2 text-xs">
                  <a href="tel:8178399885" className="font-bold text-amber-200 hover:text-white">
                    Call: +91 81783 99885
                  </a>
                  <a href="mailto:info@medrpha.com" className="text-red-100 hover:text-white">
                    Email: info@medrpha.com
                  </a>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: APPLICATION FORM */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/90 shadow-xl relative">
                
                <div className="mb-6 pb-4 border-b border-slate-100">
                  <h3 className="text-xl font-black text-slate-900 tracking-tight">
                    Partner Registration Form
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Fill in your details below and our team will get back to you with franchise terms.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* LAB / OWNER NAME */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Contact / Lab Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="Name"
                        value={form.Name}
                        onChange={handleChange}
                        placeholder="e.g. Dr. Sharma / City Diagnostics"
                        required
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                      />
                    </div>

                    {/* EMAIL */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="Email"
                        value={form.Email}
                        onChange={handleChange}
                        placeholder="e.g. partner@example.com"
                        required
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* MOBILE */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Mobile Number <span className="text-red-500">*</span>
                      </label>
                      <div className="flex">
                        <span className="px-3 flex items-center bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-xs font-bold text-slate-600">
                          +91
                        </span>
                        <input
                          type="tel"
                          maxLength={10}
                          name="Phone"
                          value={form.Phone}
                          onChange={handleChange}
                          placeholder="10-digit number"
                          required
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-r-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                        />
                      </div>
                    </div>

                    {/* CITY */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        City / Location <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="City"
                        value={form.City}
                        onChange={handleChange}
                        placeholder="e.g. Noida / Lucknow / Delhi"
                        required
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {/* STATE */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        name="State"
                        value={form.State}
                        onChange={handleChange}
                        placeholder="e.g. Uttar Pradesh"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                      />
                    </div>

                    {/* COUNTRY */}
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        name="Country"
                        value={form.Country}
                        onChange={handleChange}
                        placeholder="India"
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* FULL ADDRESS */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Lab / Clinic Center Address
                    </label>
                    <textarea
                      rows={3}
                      name="Address"
                      value={form.Address}
                      onChange={handleChange}
                      placeholder="Enter complete facility address, landmarks, and pincode..."
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all resize-none"
                    />
                  </div>

                  {/* LAB CERTIFICATION / PHOTO UPLOAD */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Lab Photo / NABL Accreditation Certificate (Optional)
                    </label>
                    <div className="border-2 border-dashed border-slate-200 hover:border-red-400 rounded-2xl p-4 text-center cursor-pointer transition-colors relative bg-slate-50">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="flex flex-col items-center justify-center gap-1.5">
                        <FaUpload className="text-slate-400 text-lg" />
                        <span className="text-xs font-semibold text-slate-600">
                          {image ? image.name : "Click to choose image or certificate file"}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          PNG, JPG, or JPEG up to 5MB
                        </span>
                      </div>
                    </div>
                    {imagePreview && (
                      <div className="mt-2 flex items-center gap-2">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="w-12 h-12 object-cover rounded-lg border border-slate-200 shadow-xs"
                        />
                        <span className="text-[11px] text-emerald-600 font-bold">
                          ✓ File selected
                        </span>
                      </div>
                    )}
                  </div>

                  {/* SUBMIT BUTTON */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-xl text-sm font-bold shadow-lg shadow-red-600/25 transition-all hover:scale-[1.01] cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {loading ? (
                        <span>Submitting Application...</span>
                      ) : (
                        <>
                          <FaHandshake size={16} />
                          <span>Submit Partnership Application</span>
                        </>
                      )}
                    </button>
                  </div>

                  <p className="text-[11px] text-center text-slate-400 pt-1">
                    By submitting, you agree to receive franchise and partnership calls from MedrphaLab.
                  </p>

                </form>

              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}